import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import SectionLoading from '@components/SectionLoading';
import {
  MeetingStatus,
  useMeetingManager,
  useMeetingStatus,
  useContentShareControls,
  useUserActivityState,
} from 'amazon-chime-sdk-component-library-react';
import { StyledLayout, StyledContent, StyledControls } from '../../styles/Styled';
import useMeetingPip from '../../hooks/useMeetingPip';
import useAudioOutputRecovery from '../../hooks/useAudioOutputRecovery';
import useAudioInputAutoSelect from '../../hooks/useAudioInputAutoSelect';
import useVideoInputAutoSelect from '../../hooks/useVideoInputAutoSelect';
import useContentShareTileIds from '../../hooks/useContentShareTileIds';
import useOutsideClickClose from '../../hooks/useOutsideClickClose';
import MeetingDock from '../MeetingDock';
import StagePanels from '../StagePanels';
import StageVideoArea from '../StageVideoArea';
import useParticipantStatuses from '../../hooks/useParticipantStatuses';
import { setVideoChatV2InMeeting } from '@containers/App/actions';
import { openPopUp } from '@containers/App/containers/PopUp/actions';
import POPUP_TYPES from '@containers/App/containers/PopUp/types';
import {
  startVideoChatV2MeetingPresence,
  stopVideoChatV2MeetingPresence,
} from '@services/meetingPresenceService';
import {
  isDeviceAccessError,
  isContentShareUserCancelError,
} from '../../utils/errorUtils';

const CAMERA_QUALITY_PROFILE = {
  width: 1280,
  height: 720,
  frameRate: 30,
  maxBandwidthKbps: 2500,
};

const SCREEN_SHARE_CAPTURE_PROFILE = {
  width: { ideal: 1600, max: 1920 },
  height: { ideal: 900, max: 1080 },
  frameRate: { ideal: 24, max: 30 },
};

const LiveMeetingStage = ({
  meetingPayload,
  users,
  initialCameraOn,
  initialAudioInputDeviceId,
  initialVideoInputDeviceId,
  initialAudioOutputDeviceId,
  onAudioOutputSelected,
  onLeave,
  onJoinFailed,
  onMeetingEnded,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const meetingManager = useMeetingManager();
  const meetingStatus = useMeetingStatus();
  const {
    isLocalUserSharing,
    isLocalShareLoading,
    toggleContentShare,
  } = useContentShareControls();
  const { isUserActive } = useUserActivityState();
  const didAutoStartVideoRef = useRef(false);
  const [joining, setJoining] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [normalLayoutMode, setNormalLayoutMode] = useState('single');
  const [cameraBusy, setCameraBusy] = useState(false);
  const [localCameraOn, setLocalCameraOn] = useState(!!initialCameraOn);
  const [showCameraMenu, setShowCameraMenu] = useState(false);
  const [cameraDevices, setCameraDevices] = useState([]);
  const [activeCameraDeviceId, setActiveCameraDeviceId] = useState('');
  const hasJoinedRef = useRef(false);
  const hasStartedMeetingRef = useRef(false);
  const hasEverBeenInMeetingRef = useRef(false);
  const didNotifyMeetingEndedRef = useRef(false);
  const onJoinFailedRef = useRef(onJoinFailed);
  const onMeetingEndedRef = useRef(onMeetingEnded);
  const joinFailedMessageRef = useRef('');
  const inMeetingRef = useRef(false);
  const videosContainerRef = useRef(null);
  const pipMenuRef = useRef(null);
  const cameraMenuRef = useRef(null);
  const pipSupported = typeof document !== 'undefined' && !!document.pictureInPictureEnabled;
  const allUsers = users || [];
  const usersWithStatuses = useParticipantStatuses(allUsers, { localCameraOn });
  const selfUser = useMemo(() => usersWithStatuses.find((user) => user?.self), [usersWithStatuses]);
  const remoteUsers = useMemo(() => usersWithStatuses.filter((user) => !user?.self), [usersWithStatuses]);

  const {
    pipMode,
    pipBusy,
    startPiP,
    stopPiP,
    handleVideoAreaClickCapture,
  } = useMeetingPip({
    meetingManager,
    pipSupported,
  });

  const allRemoteContentTileIds = useContentShareTileIds({
    audioVideo: meetingManager.meetingSession?.audioVideo,
    providerTileIds: [],
  });
  const isAnyUserSharing = isLocalUserSharing || allRemoteContentTileIds.length > 0;

  const applyCameraQualityProfile = useCallback(() => {
    const av = meetingManager.meetingSession?.audioVideo;
    if (!av) {
      return;
    }

    try {
      if (typeof av.chooseVideoInputQuality === 'function') {
        av.chooseVideoInputQuality(
          CAMERA_QUALITY_PROFILE.width,
          CAMERA_QUALITY_PROFILE.height,
          CAMERA_QUALITY_PROFILE.frameRate,
        );
      }
      if (typeof av.setVideoMaxBandwidthKbps === 'function') {
        av.setVideoMaxBandwidthKbps(CAMERA_QUALITY_PROFILE.maxBandwidthKbps);
      }
    } catch (_error) {
      // Non-fatal.
    }
  }, [meetingManager]);

  useAudioOutputRecovery({
    meetingStatus,
    meetingManager,
    initialAudioOutputDeviceId,
    onAudioOutputSelected,
  });

  useAudioInputAutoSelect({
    meetingStatus,
    meetingManager,
    initialAudioInputDeviceId,
  });

  useVideoInputAutoSelect({
    meetingStatus,
    meetingManager,
    initialVideoInputDeviceId,
    activeCameraDeviceId,
    setActiveCameraDeviceId,
    setCameraDevices,
    localCameraOn,
    applyCameraQualityProfile,
  });

  useEffect(() => {
    onJoinFailedRef.current = onJoinFailed;
  }, [onJoinFailed]);

  useEffect(() => {
    onMeetingEndedRef.current = onMeetingEnded;
  }, [onMeetingEnded]);

  useEffect(() => {
    joinFailedMessageRef.current = t('VideoChatV2.errors.unableToJoin');
  }, [t]);

  useOutsideClickClose({
    isOpen: showCameraMenu,
    onClose: () => setShowCameraMenu(false),
    containerRef: cameraMenuRef,
  });


  // Auto-enable local video once the meeting is connected.
  useEffect(() => {
    didAutoStartVideoRef.current = false;
    setLocalCameraOn(!!initialCameraOn);
  }, [initialCameraOn]);

  // Reset the auto-start guard whenever the meeting is no longer Succeeded
  // (network reconnect, session drop). This ensures startCamera() runs again
  // when MeetingStatus returns to Succeeded after a reconnect.
  useEffect(() => {
    if (meetingStatus !== MeetingStatus.Succeeded) {
      didAutoStartVideoRef.current = false;
    }
  }, [meetingStatus]);

  useEffect(() => {
    let disposed = false;
    let observedAudioVideo = null;
    let tileObserver = null;
    let localTileActiveTimeout = null;

    const cleanupLocalTileWatch = () => {
      if (localTileActiveTimeout) {
        clearTimeout(localTileActiveTimeout);
        localTileActiveTimeout = null;
      }
      if (observedAudioVideo && tileObserver) {
        observedAudioVideo.removeObserver(tileObserver);
        tileObserver = null;
      }
    };

    if (
      meetingStatus === MeetingStatus.Succeeded
      && !didAutoStartVideoRef.current
    ) {
      didAutoStartVideoRef.current = true;

      const startCamera = async () => {
        try {
          // Re-select the video device on the live session to guarantee
          // the stream is active before starting the tile. Without this
          // the tile is black because chooseVideoInputDevice was called
          // only on the prejoin session, not the started one.
          const av = meetingManager.meetingSession?.audioVideo;
          if (av) {
            const videoDevices = await av.listVideoInputDevices();
            if (disposed) return;
            if (videoDevices?.length > 0) {
              const hasInitialVideo = !!(
                initialVideoInputDeviceId
                && videoDevices.some((device) => device.deviceId === initialVideoInputDeviceId)
              );
              const initialVideoDeviceId = hasInitialVideo
                ? initialVideoInputDeviceId
                : videoDevices[0].deviceId;
              if (typeof meetingManager.startVideoInputDevice === 'function') {
                await meetingManager.startVideoInputDevice(initialVideoDeviceId);
              } else {
                await av.chooseVideoInputDevice(initialVideoDeviceId);
              }
              if (disposed) return;
              applyCameraQualityProfile();
              setActiveCameraDeviceId(initialVideoDeviceId);
              setCameraDevices(videoDevices);
            }
            if (initialCameraOn) {
              // Register a one-shot observer that restarts the local tile if it
              // never becomes active. This is event-driven: the observer fires
              // immediately on each tile update, so there is no fixed wait time.
              // The race occurs when startLocalVideoTile is called before the
              // camera MediaStream is flowing — Chime publishes the tile as
              // inactive and remote attendees never receive video. Stopping and
              // restarting the tile re-publishes it once the stream is ready.
              observedAudioVideo = av;
              localTileActiveTimeout = setTimeout(() => {
                // Safety net: if the observer never fired active=true within
                // 6 s (e.g. observer itself raced), force a restart.
                cleanupLocalTileWatch();
                if (disposed) return;
                const liveAv = meetingManager.meetingSession?.audioVideo;
                if (!liveAv) return;
                const tiles = liveAv.getAllVideoTiles?.() ?? [];
                const localActive = tiles.some(
                  (t) => t.state()?.localTile && t.state()?.active,
                );
                if (!localActive) {
                  liveAv.stopLocalVideoTile();
                  liveAv.startLocalVideoTile();
                }
              }, 6000);

              tileObserver = {
                videoTileDidUpdate(tileState) {
                  if (!tileState.localTile) return;
                  if (tileState.active) {
                    // Tile is live — nothing to fix.
                    cleanupLocalTileWatch();
                  } else {
                    // Tile exists but has no active stream — restart it.
                    cleanupLocalTileWatch();
                    if (disposed) return;
                    av.stopLocalVideoTile();
                    av.startLocalVideoTile();
                  }
                },
              };
              av.addObserver(tileObserver);

              av.startLocalVideoTile();
              setLocalCameraOn(true);
            } else {
              av.stopLocalVideoTile();
              setLocalCameraOn(false);
            }
          }
        } catch (_e) {
          // Non-fatal.
        }
      };

      startCamera();
    }

    return () => {
      disposed = true;
      cleanupLocalTileWatch();
    };
  }, [
    applyCameraQualityProfile,
    initialCameraOn,
    initialVideoInputDeviceId,
    meetingManager,
    meetingStatus,
  ]);

  useEffect(() => {
    hasJoinedRef.current = false;
  }, []);

  useEffect(() => {
    const joinMeeting = async () => {
      if (hasJoinedRef.current) {
        return;
      }

      try {
        hasJoinedRef.current = true;
        setJoining(true);
        await meetingManager.start();
        hasStartedMeetingRef.current = true;
        setJoining(false);
      } catch (error) {
        hasStartedMeetingRef.current = false;
        setJoining(false);
        dispatch(setVideoChatV2InMeeting(false));
        if (isDeviceAccessError(error)) {
          dispatch(openPopUp(POPUP_TYPES.MEDIA_DEVICES_PERMISSIONS));
        }
        if (onJoinFailedRef.current) {
          onJoinFailedRef.current(error?.message || joinFailedMessageRef.current);
        }
      }
    };

    joinMeeting();

    return () => {
      dispatch(setVideoChatV2InMeeting(false));
      meetingManager.leave();
    };
  }, [dispatch, meetingManager]);

  useEffect(() => {
    const inMeeting = meetingStatus === MeetingStatus.Succeeded;

    if (inMeeting && hasStartedMeetingRef.current) {
      hasEverBeenInMeetingRef.current = true;
    }

    const isTerminalMeetingStatus = (
      meetingStatus === MeetingStatus.Ended
      || meetingStatus === MeetingStatus.Left
      || meetingStatus === MeetingStatus.TerminalFailure
      || meetingStatus === MeetingStatus.JoinedFromAnotherDevice
    );

    if (
      isTerminalMeetingStatus
      && hasEverBeenInMeetingRef.current
      && !didNotifyMeetingEndedRef.current
    ) {
      didNotifyMeetingEndedRef.current = true;
      if (onMeetingEndedRef.current) {
        onMeetingEndedRef.current();
      }
    }

    if (inMeeting && !inMeetingRef.current) {
      inMeetingRef.current = true;
      dispatch(setVideoChatV2InMeeting(true));
      startVideoChatV2MeetingPresence();
    } else if (isTerminalMeetingStatus && inMeetingRef.current) {
      inMeetingRef.current = false;
      dispatch(setVideoChatV2InMeeting(false));
      stopVideoChatV2MeetingPresence();
    }
  }, [dispatch, meetingStatus]);

  useEffect(() => () => {
    stopVideoChatV2MeetingPresence();
  }, []);

  if (joining || meetingStatus === MeetingStatus.Loading) {
    return <SectionLoading />;
  }

  const toggleParticipants = () => {
    setShowParticipants((prev) => !prev);
  };

  const toggleNormalLayoutMode = () => {
    setNormalLayoutMode((prev) => (prev === 'single' ? 'side-by-side' : 'single'));
  };

  const handleCameraToggle = async () => {
    if (cameraBusy) {
      return;
    }

    setCameraBusy(true);
    try {
      const av = meetingManager.meetingSession?.audioVideo;

      if (!localCameraOn) {
        if (av) {
          const videoDevices = await av.listVideoInputDevices();
          setCameraDevices(videoDevices || []);
          if (videoDevices?.length > 0) {
            const nextDeviceId = activeCameraDeviceId || videoDevices[0].deviceId;
            if (typeof meetingManager.startVideoInputDevice === 'function') {
              await meetingManager.startVideoInputDevice(nextDeviceId);
            } else {
              await av.chooseVideoInputDevice(nextDeviceId);
            }
            applyCameraQualityProfile();
            setActiveCameraDeviceId(nextDeviceId);
          }
          av.startLocalVideoTile();
        }
        setLocalCameraOn(true);
      } else {
        if (av) {
          av.stopLocalVideoTile();
        }
        setLocalCameraOn(false);
      }
    } catch (_error) {
      // Non-fatal.
    } finally {
      setCameraBusy(false);
    }
  };

  const openCameraMenu = async () => {
    const av = meetingManager.meetingSession?.audioVideo;
    if (!av) {
      setShowCameraMenu((prev) => !prev);
      return;
    }

    try {
      const devices = await av.listVideoInputDevices();
      setCameraDevices(devices || []);
    } catch (_error) {
      setCameraDevices([]);
    }

    setShowCameraMenu((prev) => !prev);
  };

  const handleChooseCameraDevice = async (deviceId) => {
    const av = meetingManager.meetingSession?.audioVideo;
    if (!av) {
      setShowCameraMenu(false);
      return;
    }

    try {
      if (typeof meetingManager.startVideoInputDevice === 'function') {
        await meetingManager.startVideoInputDevice(deviceId);
      } else {
        await av.chooseVideoInputDevice(deviceId);
      }
      applyCameraQualityProfile();
      setActiveCameraDeviceId(deviceId);

      if (localCameraOn) {
        av.startLocalVideoTile();
      }
    } catch (_error) {
      // Non-fatal.
    }

    setShowCameraMenu(false);
  };

  const handleContentShareToggle = async () => {
    if (isLocalShareLoading) {
      return;
    }

    // Stopping.
    if (isLocalUserSharing) {
      try {
        await toggleContentShare();
        applyCameraQualityProfile();
      } catch (_error) {
        // Non-fatal.
      }
      return;
    }

    // Starting — capture the screen ourselves so we can enforce resolution
    // constraints that are clear enough to show monitors across all OSes, and
    // so we can surface a meaningful error when macOS blocks the permission.
    let stream;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({
        video: SCREEN_SHARE_CAPTURE_PROFILE,
        audio: true,
      });

      // Favor text/detail readability while keeping motion acceptable.
      const [videoTrack] = stream.getVideoTracks();
      if (videoTrack && typeof videoTrack.contentHint === 'string') {
        videoTrack.contentHint = 'detail';
      }
    } catch (error) {
      if (isContentShareUserCancelError(error)) {
        return;
      }

      if (error?.name === 'NotAllowedError') {
        dispatch(openPopUp(POPUP_TYPES.MEDIA_DEVICES_PERMISSIONS));
        const platform = (navigator.userAgentData?.platform ?? navigator.platform ?? '').toLowerCase();
        const isMac = platform.includes('mac');
        if (isMac) {
          console.warn('[ContentShare] Screen Recording permission denied by macOS system policy.', error);
          toast.error(
            t('Video.screenShareBlockedMac'),
            { autoClose: 10000, position: 'bottom-right' },
          );
        } else {
          console.warn('[ContentShare] getDisplayMedia not allowed.', error);
        }
      }
      return;
    }

    try {
      // Pass the stream directly; the SDK calls startContentShare(stream) and
      // takes ownership of the tracks — do NOT stop them here.
      await toggleContentShare(stream);
    } catch (_error) {
      // If the SDK rejects, release the tracks so the OS indicator disappears.
      stream.getTracks().forEach((t) => t.stop());
    }
  };

  const handlePiPToggle = async () => {
    if (pipMode) {
      await stopPiP();
      return;
    }
    await startPiP('focus', videosContainerRef);
  };

  const pipToggleDisabled = pipBusy || !pipSupported;

  const onVideoAreaClickCapture = (event) => handleVideoAreaClickCapture(event);

  const renderNoRemoteView = () => {
    const cameraOffUsers = [...remoteUsers];
    if (!localCameraOn && selfUser) {
      cameraOffUsers.unshift(selfUser);
    }

    if (cameraOffUsers.length === 0) {
      return (
        <div
          style={{
            width: '100%',
            height: '100%',
            minHeight: 260,
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 14,
            opacity: 0.88,
          }}
        >
          {t('VideoChatV2.waitingForParticipants')}
        </div>
      );
    }

    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          minHeight: 260,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          padding: 24,
          boxSizing: 'border-box',
          flexWrap: 'wrap',
        }}
      >
        {cameraOffUsers.map((user) => {
          const name = user.fullName || user.name || (user.self ? t('VideoChatV2.you') : t('VideoChatV2.participant'));
          const initials = name
            .split(' ')
            .filter(Boolean)
            .map((part) => part[0])
            .join('')
            .toUpperCase()
            .slice(0, 2) || 'P';

          return (
            <div
              key={user.participantId || name}
              style={{
                width: 240,
                height: 180,
                borderRadius: 12,
                background: '#1f2937',
                border: '1px solid rgba(255,255,255,0.12)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
              }}
            >
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  background: '#374151',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 24,
                  fontWeight: 700,
                }}
              >
                {initials}
              </div>
              <div style={{ marginTop: 10, fontSize: 14, fontWeight: 600 }}>{name}</div>
              <div style={{ marginTop: 4, fontSize: 12, opacity: 0.8 }}>{t('VideoChatV2.camera.off')}</div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <StyledLayout>
      <StyledContent>
        <div className="stage-main">
          <div className="videos" ref={videosContainerRef} onClickCapture={onVideoAreaClickCapture}>
            <StageVideoArea
              users={usersWithStatuses}
              localCameraOn={localCameraOn}
              noRemoteVideoView={renderNoRemoteView()}
              normalLayoutMode={normalLayoutMode}
              onToggleNormalLayoutMode={toggleNormalLayoutMode}
              isAnyUserSharing={isAnyUserSharing}
            />
          </div>
          <StagePanels showParticipants={showParticipants} showChat={false} users={usersWithStatuses} />
        </div>
        <StyledControls className="controls" $active={!!isUserActive}>
          <MeetingDock
            meetingPayload={meetingPayload}
            initialAudioOutputDeviceId={initialAudioOutputDeviceId}
            onAudioOutputSelected={onAudioOutputSelected}
            localCameraOn={localCameraOn}
            cameraBusy={cameraBusy}
            handleCameraToggle={handleCameraToggle}
            openCameraMenu={openCameraMenu}
            showCameraMenu={showCameraMenu}
            cameraDevices={cameraDevices}
            activeCameraDeviceId={activeCameraDeviceId}
            handleChooseCameraDevice={handleChooseCameraDevice}
            isLocalUserSharing={isLocalUserSharing}
            handleContentShareToggle={handleContentShareToggle}
            isLocalShareLoading={isLocalShareLoading}
            pipMenuRef={pipMenuRef}
            cameraMenuRef={cameraMenuRef}
            pipMode={pipMode}
            handlePiPToggle={handlePiPToggle}
            pipToggleDisabled={pipToggleDisabled}
            onLeave={onLeave}
            showParticipants={showParticipants}
            toggleParticipants={toggleParticipants}
            showChat={false}
          />
        </StyledControls>
      </StyledContent>
    </StyledLayout>
  );
};

export default LiveMeetingStage;
